<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import GoogleIcon from '@/assets/icons/google.svg';
import { googleSignIn, emailSignIn } from '@/auth';
import { VTextField } from 'vuetify/lib/components/index.mjs';
import { validationEmail } from '@/utils/validation/text-field';
import { TERMS_OF_SERVICE_URL, PRIVACY_POLICY_URL } from '@/constants/assets';

const route = useRoute();

const state = reactive({
  email: '',
  loading: false,
  signInEmailBtnLoader: false,
  isFormValid: false,
});

const emailInputRef = ref<VTextField | null>(null);
const signInHandler = async (email: string) => {
  if (!state.isFormValid) {
    emailInputRef.value?.$el?.querySelector('input')?.focus();
    return;
  }

  state.signInEmailBtnLoader = true;
  await emailSignIn(email.toLowerCase(), route.query.referralCode as string);
  state.email = '';
  state.signInEmailBtnLoader = false;
};

const signInGoogleHandler = () => {
  const referralCode = route.query.referralCode as string;
  googleSignIn(referralCode || '');
};

const onValidateEmail = () => {
  return state.email ? validationEmail(state.email) : true;
};
</script>

<template>
  <Loader v-if="state.loading" class="h-full" />
  <div
    v-else
    class="flex items-center justify-center max-lg:py-9 max-lg:px-6 lg:p-12 bg-white rounded-2xl shadow-md"
  >
    <div class="flex flex-col gap-y-6 w-full">
      <div class="flex flex-col align-center gap-y-3">
        <p class="font-nunito text-2xl sm:text-[32px] font-bold text-center">
          Get started
        </p>
        <p class="text-grey-90 typo-body1 max-w-[231px] text-center">
          Make sure to continue with an email you will use to record your
          meetings
        </p>
      </div>
      <v-btn
        class="signin-form__google-btn shadow"
        @click="signInGoogleHandler"
      >
        <template #prepend>
          <GoogleIcon />
        </template>
        <span>Continue with Google</span>
      </v-btn>
      <div class="flex items-center flex-nowrap gap-x-2 w-full">
        <div class="flex grow h-[1px] bg-grey-slate-300" />
        <span class="typo-body2 text-grey-90 whitespace-nowrap"
          >or use magic link</span
        >
        <div class="flex grow h-[1px] bg-grey-slate-300" />
      </div>
      <v-form
        v-model="state.isFormValid"
        validate-on="blur"
        class="flex flex-col gap-y-2"
        @submit.prevent="signInHandler(state.email)"
      >
        <v-text-field
          ref="emailInputRef"
          v-model="state.email"
          variant="outlined"
          hide-details="auto"
          :disabled="state.signInEmailBtnLoader"
          :rules="[onValidateEmail]"
          placeholder="name@mail.com"
          class="signin-form__email-input"
        />
        <v-btn
          :loading="state.signInEmailBtnLoader"
          :disabled="state.signInEmailBtnLoader"
          type="submit"
          variant="tonal"
          class="signin-form__submit-btn"
          block
          text="Continue with Email"
        />
      </v-form>
      <p class="text-center typo-body2 text-grey-90">
        By signing in, you agree to the<br />
        <a
          :href="TERMS_OF_SERVICE_URL"
          rel="noopener nofollow"
          target="_blank"
          class="text-primary hover:underline"
        >
          Terms of Service
        </a>
        and
        <a
          :href="PRIVACY_POLICY_URL"
          rel="noopener nofollow"
          target="_blank"
          class="text-primary hover:underline"
        >
          Privacy Policy
        </a>
      </p>
    </div>
  </div>
</template>

<style>
.signin-form__google-btn.v-btn {
  --v-btn-height: 48px;
  @apply px-2 font-inter text-sm leading-[150%] font-semibold;
}

.signin-form__google-btn .v-btn__prepend {
  @apply ml-0 mr-2;
}

.signin-form__email-input.v-input .v-field__field input {
  @apply py-[13px] px-2 typo-body1 text-black;
}

.signin-form__email-input.v-input .v-field__field input::placeholder {
  @apply text-grey-90 opacity-100;
}

.signin-form__submit-btn.v-btn {
  --v-btn-height: 48px;
  @apply px-[10px] py-[14px] typo-body1 font-semibold text-white bg-primary;
}
</style>
